import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Button, { ButtonColor, ButtonSize, ButtonVariant } from 'src/components/atoms/Button';
import { Card } from 'src/components/atoms/Card';
import Gap from 'src/components/atoms/Gap';
import { Row } from 'src/components/atoms/Row';
import Text, { TextVariant } from 'src/components/atoms/Text';

import { useFolderPage } from 'src/components/folders/FolderPageContext';
import CopyableField from 'src/components/folders/MetadataModal/components/CopyableField';

import { configVars } from 'src/helpers';
import { useUserFeature } from 'src/hooks';
import useSnackbarProvider from 'src/hooks/useSnackbarProvider';

// ITL-2824: server-computed claimability for the new Claim Fraud /
// Claim IV / Simulate buttons. Shape mirrors `ManualClaim.claimability/2`
// on the BE. Inline type — no SDK extension for one-off dev tooling.
type WorkflowClaimability = {
  enabled: boolean;
  status_message: string | null;
  assigned_to: string | null;
};
type ClaimabilityResponse = {
  fraud: WorkflowClaimability;
  iv: WorkflowClaimability;
  voa: WorkflowClaimability;
  // Present only on POST /claim_review success responses. The BE owns
  // workflow-aware routing: fraud → /my-work/{folder_id}, IV →
  // /my-work/incomeVerification/{iv_review_id}/{entry_id}, VOA →
  // /my-work/asset-verification/{review_item_id}/{entry_id}. FE just
  // follows the path.
  redirect_path?: string;
};

const SIMULATE_STATUS_MSG =
  'Click to materialise an IV submission + review for the selected entry.';

export const FolderDeveloperPanel: FC = () => {
  const hasDevDashboardAccess = useUserFeature('dev_dashboard');
  const { folderId } = useParams();
  const { selectedEntryId } = useFolderPage();
  const { showSnackbar, VariantType } = useSnackbarProvider();
  const navigate = useNavigate();

  const [claimability, setClaimability] = useState<ClaimabilityResponse | null>(null);

  // ITL-2824: stable ref for the snackbar API.
  // `useSnackbarProvider()` returns a fresh `showSnackbar` reference on
  // every render. Including it (or `VariantType.error`) in the
  // claimability useEffect's deps array creates an infinite re-fetch
  // loop: fetch → setClaimability → re-render → new showSnackbar ref →
  // useEffect re-fires → loop. The ref is updated on every render so
  // the latest function is always callable; the useEffect deps only
  // reference `selectedEntryId`.
  const snackbarRef = useRef({
    showSnackbar,
    errorVariant: VariantType.error,
    successVariant: VariantType.success
  });
  snackbarRef.current = {
    showSnackbar,
    errorVariant: VariantType.error,
    successVariant: VariantType.success
  };

  // Re-fetch claimability whenever the user switches submission tabs.
  // Returns null on 404/5xx so the buttons render disabled with the
  // "Loading..." fallback.
  useEffect(() => {
    let cancelled = false;
    // ITL-3042: hooks run even when the access early-return below renders
    // null — gate the fetch too, or every user 403s on folder page load.
    if (!selectedEntryId || !hasDevDashboardAccess) {
      setClaimability(null);
      return;
    }

    const url = `${configVars.api_url}/dev_dashboard/review_claimability/${selectedEntryId}`;

    fetch(url, { credentials: 'include' })
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          // Re-check after the await: a slow error response could otherwise
          // pop a stale snackbar / clear state after the user switched tabs.
          if (cancelled) return;
          setClaimability(null);
          snackbarRef.current.showSnackbar(
            snackbarRef.current.errorVariant,
            body?.error ?? `Failed to load claimability (HTTP ${res.status})`
          );
          return;
        }
        const data = (await res.json()) as ClaimabilityResponse;
        setClaimability(data);
      })
      .catch(() => {
        if (!cancelled) setClaimability(null);
      });

    return () => {
      cancelled = true;
    };
    // Intentionally omitting `snackbarRef` (stable ref, never changes) and
    // the raw `showSnackbar`/`VariantType` to avoid the re-fetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntryId, hasDevDashboardAccess]);

  const claimReview = useCallback(
    async (workflow: 'fraud' | 'iv' | 'voa') => {
      if (!selectedEntryId || !folderId) return;

      try {
        const res = await fetch(`${configVars.api_url}/dev_dashboard/claim_review`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entry_id: selectedEntryId, workflow })
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          snackbarRef.current.showSnackbar(
            snackbarRef.current.errorVariant,
            body?.error ?? `Claim failed (HTTP ${res.status})`
          );
          return;
        }

        const data = (await res.json()) as ClaimabilityResponse;
        setClaimability(data);
        // Follow the BE-supplied redirect (workflow-aware) when present;
        // fall back to /my-work/{folder_id} for safety.
        navigate(data.redirect_path ?? `/my-work/${folderId}`);
      } catch (_error) {
        snackbarRef.current.showSnackbar(
          snackbarRef.current.errorVariant,
          'Claim request failed. Please try again.'
        );
      }
    },
    [selectedEntryId, folderId, navigate]
  );

  const handleClaimFraud = useCallback(() => claimReview('fraud'), [claimReview]);
  const handleClaimIv = useCallback(() => claimReview('iv'), [claimReview]);
  const handleClaimVoa = useCallback(() => claimReview('voa'), [claimReview]);

  const handleSimulate = useCallback(async () => {
    if (!selectedEntryId) return;

    try {
      const res = await fetch(`${configVars.api_url}/dev_dashboard/simulate_income_audited`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry_id: selectedEntryId })
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        snackbarRef.current.showSnackbar(
          snackbarRef.current.errorVariant,
          body?.error ?? `Simulate failed (HTTP ${res.status})`
        );
        return;
      }

      const data = (await res.json()) as ClaimabilityResponse;
      setClaimability(data);
      snackbarRef.current.showSnackbar(
        snackbarRef.current.successVariant,
        'IncomeAudited simulated. IV submission + review materialised.'
      );
    } catch (_error) {
      snackbarRef.current.showSnackbar(
        snackbarRef.current.errorVariant,
        'Simulate request failed. Please try again.'
      );
    }
  }, [selectedEntryId]);

  if (!hasDevDashboardAccess) return null;

  return (
    <Card data-testid="folder-developer-panel">
      <Row direction="column" gap={1}>
        <Row.Col>
          <Text variant={TextVariant.h3}>Developer</Text>
        </Row.Col>

        <Row.Col>
          <Text variant={TextVariant.h4}>IDs</Text>
        </Row.Col>
        {folderId && <CopyableField title="Folder ID" content={folderId} />}
        {selectedEntryId ? (
          <CopyableField title="Entry ID" content={selectedEntryId} />
        ) : (
          <Row.Col>
            <Text variant={TextVariant.small}>
              Select a submission tab above to see its entry ID.
            </Text>
          </Row.Col>
        )}

        <Gap height={1} />

        {selectedEntryId ? (
          <>
            <ClaimButtonRow
              testId="dev-claim-fraud-review-button"
              label="Claim Fraud Review"
              workflowState={claimability?.fraud}
              onClick={handleClaimFraud}
            />
            {/* Simulate appears above Claim IV in the row order — it's the
                "create the IV review" step that must run before the IV review
                can be claimed. */}
            <SimulateButtonRow onClick={handleSimulate} />
            <ClaimButtonRow
              testId="dev-claim-iv-review-button"
              label="Claim IV Review"
              workflowState={claimability?.iv}
              onClick={handleClaimIv}
            />
            {/* VOA needs no Simulate step here: seed the review with the
                dev-dashboard VOA scenario (ITL-3002) or publish a
                needs-review event, then claim it. */}
            <ClaimButtonRow
              testId="dev-claim-voa-review-button"
              label="Claim VOA Review"
              workflowState={claimability?.voa}
              onClick={handleClaimVoa}
            />
          </>
        ) : (
          <Row.Col>
            <Text variant={TextVariant.small}>
              Select a submission tab above to claim a review or simulate an IncomeAudited event.
            </Text>
          </Row.Col>
        )}
      </Row>
    </Card>
  );
};

// One reusable row for the Claim Fraud + Claim IV buttons. Renders the
// button on the left and the BE-supplied status message on the right.
const ClaimButtonRow: FC<{
  testId: string;
  label: string;
  workflowState: WorkflowClaimability | undefined;
  onClick: () => void;
}> = ({ testId, label, workflowState, onClick }) => {
  const enabled = workflowState?.enabled ?? false;
  const statusMessage = workflowState?.status_message ?? 'Loading...';
  return (
    <Row gap={1} alignItems="center">
      <Row.Col>
        <Button
          data-testid={testId}
          name={testId}
          variant={ButtonVariant.contained}
          color={ButtonColor.primary}
          size={ButtonSize.normal}
          isDisabled={!enabled}
          onClick={onClick}
        >
          {label}
        </Button>
      </Row.Col>
      <Row.Col>
        <Text variant={TextVariant.small}>{statusMessage}</Text>
      </Row.Col>
    </Row>
  );
};

const SimulateButtonRow: FC<{ onClick: () => void }> = ({ onClick }) => (
  <Row gap={1} alignItems="center">
    <Row.Col>
      <Button
        data-testid="dev-simulate-income-audited-button"
        name="dev-simulate-income-audited-button"
        variant={ButtonVariant.contained}
        color={ButtonColor.primary}
        size={ButtonSize.normal}
        onClick={onClick}
      >
        Simulate IncomeAudited
      </Button>
    </Row.Col>
    <Row.Col>
      <Text variant={TextVariant.small}>{SIMULATE_STATUS_MSG}</Text>
    </Row.Col>
  </Row>
);

export default FolderDeveloperPanel;
